import React, { useEffect, useState, useMemo } from "react";
import MDAvatar from "components/MDAvatar";
import Axios from "axios";
import Cookies from "js-cookie";
import IconButton from "@mui/material/IconButton";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import {
  useProducts,
  useDeleteProduct,
  useUpdateProduct,
  useRefetchProducts,
} from "api/useProducts.ts";
import { useCategories } from "api/useCategories.ts";
import { useMosques } from "api/useMosques.ts";

export default function productsTableData() {
  const { data: fetchedProducts, isLoading } = useProducts();
  const deleteProduct = useDeleteProduct();
  const updateProduct = useUpdateProduct();
  const { data: mosques } = useMosques();
  const { refetch: refetchProducts } = useRefetchProducts();
  const { data: categories } = useCategories();
  const [products, setProducts] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isFileModalOpen, setIsFileModalOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success", // "success" | "error"
  });

  const [editedProduct, setEditedProduct] = useState({
    productId: "",
    productName: {
      en: "",
      ar: "",
    },
    price: 0,
    stockQuantity: 0,
    category: {
      en: "",
      ar: "",
    },
    description: {
      en: "",
      ar: "",
    },
  });

  const handleFileChange = (e) => {
    setImagePreview(URL.createObjectURL(e.target.files[0]));
    setSelectedFile(e.target.files[0]);
  };

  const handleOpenFileModal = (productId) => {
    setSelectedId(productId);
    setSelectedFile(null);
    setIsFileModalOpen(true);
  };
  const closeFileModal = () => {
    setIsFileModalOpen(false);
    setSelectedFile(null);
  };
  const handleUploadImage = async () => {
    if (!selectedFile) {
      setNotification({
        open: true,
        message: "No file selected",
        severity: "error",
      });
      return;
    }
    setIsUploading(true);
    try {
      const token = Cookies.get("authToken");
      if (!token) {
        throw new Error("Unauthorized access");
      }
      const response = await Axios.patch(
        `${process.env.REACT_APP_API_URL}/products/update-image/${selectedId}`,
        { photo: selectedFile },
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setNotification({
        open: true,
        message: "Image uploaded successfully.",
        severity: "success",
      });
      // Reset image preview on successful upload
      setImagePreview(null);
      setSelectedFile(null);
      refetchProducts();
      closeFileModal();
    } catch (error) {
      console.error("Error uploading image:", error.response || error.message);
      setNotification({
        open: true,
        message: "Failed to upload image. Please try again.",
        severity: "error",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const openDeleteModal = (productId) => {
    setSelectedId(productId);
    setIsDeleteModalOpen(true);
  };

  const handleCloseNotification = () => {
    setNotification((prev) => ({ ...prev, open: false }));
  };

  const handleEditClick = (product) => {
    setEditedProduct({
      productId: product._id,
      productName: product?.name,
      price: product.price,
      stockQuantity: product.quantity,
      category: product.category,
      description: product.description,
    });
    editedProduct && setIsModalOpen(true);
  };

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditedProduct({
      productId: "",
      productName: {
        en: "",
        ar: "",
      },
      price: 0,
      stockQuantity: 0,
      category: {
        _id: "",
        name: {
          ar: "",
          en: "",
        },
      },
      description: {
        en: "",
        ar: "",
      },
    });
  };
  const handleInputChange = (event) => {
    const { name, value } = event.target;
    const keys = name.split(".");
    if (keys.length === 2) {
      setEditedProduct((prev) => ({
        ...prev,
        [keys[0]]: {
          ...prev[keys[0]],
          [keys[1]]: value,
        },
      }));
    } else {
      setEditedProduct((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleDeleteProduct = async () => {
    if (!selectedId) return;

    deleteProduct.mutate(selectedId, {
      onSuccess: () => {
        setNotification({
          open: true,
          message: "Product deleted successfully.",
          severity: "success",
        });
        closeDeleteModal();
      },
      onError: (error) => {
        console.error("Error deleting product:", error);
        setNotification({
          open: true,
          message: "Failed to delete product. Please try again.",
          severity: "error",
        });
      },
    });
  };
  // Handling Save Changes
  const handleSaveChanges = async () => {
    const originalProduct = products.find((p) => p._id === editedProduct.productId);

    const updatedFields = Object.keys(editedProduct).reduce((changes, key) => {
      if (editedProduct[key] !== originalProduct[key]) {
        changes[key] = editedProduct[key];
      }
      return changes;
    }, {});

    if (Object.keys(updatedFields).length === 0) {
      setNotification({
        open: true,
        message: "No changes detected.",
        severity: "info",
      });
      return;
    }

    // Updated Product Data
    const updatedProduct = {
      _id: editedProduct.productId,
      name: editedProduct.productName,
      price: editedProduct.price,
      category: editedProduct.category,
      quantity: editedProduct.stockQuantity,
      description: editedProduct.description,
    };

    updateProduct.mutate(updatedProduct, {
      onSuccess: () => {
        setNotification({
          open: true,
          message: "Product updated successfully.",
          severity: "success",
        });
        handleCloseModal();
      },
      onError: (error) => {
        console.error("Error updating product:", error);
        setNotification({
          open: true,
          message: "Failed to update product. Please try again.",
          severity: "error",
        });
      },
    });
  };

  useEffect(() => {
    !isLoading && setProducts(fetchedProducts);
  }, [fetchedProducts, isLoading]);

  useEffect(() => {}, [mosques]);

  const rows = useMemo(() => {
    return isLoading
      ? Array(10)
          .fill(0)
          .map((_, index) => ({
            productId: index + 1,
            productName: "Loading...",
            productimg: <MDAvatar size="xl" />,
            category: "Loading...",
            description: "Loading...",
            price: "Loading...",
            stockQuantity: "Loading...",
            releaseDate: "Loading...",
            action: <div className="flex justify-center space-x-2">Loading...</div>,
          }))
      : Array.isArray(products) &&
          products.map((product) => ({
            productId: product._id,
            productName: (
              <div className="max-w-[100px] truncate">
                <p
                  className="font-semibold"
                  title={`${product?.name.en || ""} | ${product?.name.ar || ""}`}
                >
                  {`${product?.name.en || ""} | ${product?.name.ar || ""}`}
                </p>
              </div>
            ),
            productimg: (
              <MDAvatar
                src={product.image || "https://via.placeholder.com/150"}
                name={product?.name.en}
                size="xl"
              />
            ),
            category: `${product.category?.name.en || ""} | ${product.category?.name.ar || ""}`,
            description: `${product.description.en || ""} | ${product.description.ar || ""}`,
            price: `$${product.price}`,
            stockQuantity: product.quantity,
            releaseDate: new Date(product.createdAt).toLocaleDateString(),
            action: (
              <div className="flex justify-center space-x-2">
                <IconButton color="primary" size="medium" onClick={() => handleEditClick(product)}>
                  <EditIcon fontSize="medium" />
                </IconButton>
                <IconButton
                  color="secondary"
                  size="medium"
                  onClick={() => handleOpenFileModal(product._id)}
                >
                  <AddPhotoAlternateIcon fontSize="medium" />
                </IconButton>
                <IconButton
                  color="error"
                  size="medium"
                  onClick={() => openDeleteModal(product._id)}
                >
                  <DeleteIcon fontSize="medium" />
                </IconButton>
              </div>
            ),
          }));
  }, [isLoading, products]);

  return {
    columns: [
      { Header: "ID", accessor: "productId", align: "center" },
      { Header: "product Image", accessor: "productimg", align: "center" },
      { Header: "Product Name", accessor: "productName", align: "center" },
      { Header: "Description", accessor: "description", align: "center" },
      { Header: "Category", accessor: "category", align: "center" },
      { Header: "Price", accessor: "price", align: "center" },
      { Header: "Stock Quantity", accessor: "stockQuantity", align: "center" },
      { Header: "Release Date", accessor: "releaseDate", align: "center" },
      { Header: "Actions", accessor: "action", align: "center" },
    ],
    rows,
    editedProduct,
    handleCloseModal,
    isModalOpen,
    notification,
    setNotification,
    handleSaveChanges,
    handleCloseNotification,
    handleInputChange,
    handleDeleteProduct,
    isDeleteModalOpen,
    closeDeleteModal,
    categories,
    handleUploadImage,
    handleFileChange,
    isFileModalOpen,
    closeFileModal,
    imagePreview,
    setImagePreview,
    isUploading,
  };
}
